'use client';

/**
 * Spec 118 §3.1 (CON-321) — "Submit a server" on the public directory.
 *
 * ONE field, the URL. Every field a form asks for is a field a submitter
 * can get wrong, and discovery works the rest out — so the dialog holds a
 * single input and a single submit, and the interesting part is what it
 * says back.
 *
 * Three shapes of answer, all rendered INSIDE the dialog (spec 103 §3 — a
 * failed or refused submit never becomes a toast and never escalates to
 * the route error boundary):
 *
 *   accepted     "We'll take a look — it shows up once reviewed."
 *   duplicate    the sentence plus a link to the server we already hold
 *   rate_limited which of the §3.4 limits was hit, and when to retry
 *
 * plus the api's own ValidationError sentence for a bad scheme or a
 * private host. The outcome → copy mapping is pure and lives in
 * lib/mcp-directory-copy.ts, under test, not here.
 *
 * The action arrives as a PROP, never an import: this file is a
 * presentational component and `check:component-layering` forbids a
 * component importing a `'use server'` module (it is also what lets the
 * story render without a request scope).
 *
 * `defaultOpen` is how `?submit=1` opens the dialog on load. The query is
 * read on the SERVER (the page is already `force-dynamic`) rather than
 * with `useSearchParams`, so the component carries no router dependency
 * and Storybook can mount it as-is.
 */
import { PillButton } from '@repo/marketing-ui';
import { Button } from '@repo/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/ui/dialog';
import { StatusBanner } from '@repo/ui/components/ui/status-banner';
import Link from 'next/link';
import { useActionState, useState } from 'react';
import { SubmitButton } from '@/components/async-button';
import { MCP_DIRECTORY, SUBMIT_IDLE, type SubmitServerState } from '@/lib/mcp-directory-copy';

const S = MCP_DIRECTORY.submit;

export interface SubmitServerDialogProps {
  /** The `'use server'` action the page owns; taken as a prop (see above). */
  submitAction: (previous: SubmitServerState, formData: FormData) => Promise<SubmitServerState>;
  /** `?submit=1` was on the URL — open on load. */
  defaultOpen?: boolean;
}

export function SubmitServerDialog({ submitAction, defaultOpen = false }: SubmitServerDialogProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <PillButton size="md" scheme="accent" onClick={() => setOpen(true)} data-testid="mcp-submit-open">
        {S.cta}
      </PillButton>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="md:max-w-lg" data-testid="mcp-submit-dialog">
          <DialogHeader>
            <DialogTitle>{S.title}</DialogTitle>
            <DialogDescription>{S.description}</DialogDescription>
          </DialogHeader>
          {/* Remounted on every open, so a previous answer never greets
              the next submitter. */}
          <SubmitServerForm submitAction={submitAction} onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

/** The form half — exported so a story can show each answer on its own. */
export function SubmitServerForm({
  submitAction,
  onDone,
  initialState = SUBMIT_IDLE,
}: {
  submitAction: SubmitServerDialogProps['submitAction'];
  onDone?: () => void;
  /** Seed for a story or a test that wants a settled answer on screen. */
  initialState?: SubmitServerState;
}) {
  const [state, formAction] = useActionState(submitAction, initialState);

  // An accepted or duplicate submission is finished: showing the field
  // again invites a second copy of the row a person just sent — and the
  // one action left is Close (CON-951): there is nothing to cancel.
  if (state.status === 'ok') {
    return (
      <div className="flex flex-col gap-4">
        <StatusBanner variant="success" data-testid="mcp-submit-result">
          <p>{state.message}</p>
          {state.href ? (
            <p className="mt-1">
              <Link href={state.href} className="underline underline-offset-2">
                {state.linkLabel}
              </Link>
            </p>
          ) : null}
        </StatusBanner>
        <DialogFooter>
          <Button type="button" onClick={onDone} data-testid="mcp-submit-close">
            {S.close}
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <form action={formAction} className="flex flex-col gap-4">
      <label className="flex flex-col gap-1" htmlFor="mcp-submit-url">
        <span className="font-label text-label-sm text-text-caption-1">{S.urlLabel}</span>
        <input
          id="mcp-submit-url"
          name="url"
          type="url"
          required
          maxLength={2048}
          inputMode="url"
          autoComplete="off"
          placeholder={S.urlPlaceholder}
          aria-describedby="mcp-submit-url-hint"
          className="rounded-xxs border border-input bg-surface-field px-4 py-3 font-label text-label-md text-text-heading-1 placeholder:text-text-caption-1 focus:border-border-action focus:outline-none"
        />
        <span id="mcp-submit-url-hint" className="font-label text-label-sm text-text-caption-1">
          {S.urlHint}
        </span>
      </label>

      {state.status === 'error' ? (
        <StatusBanner variant="error" data-testid="mcp-submit-result">
          {state.message}
        </StatusBanner>
      ) : null}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          {S.cancel}
        </Button>
        <SubmitButton pendingLabel={S.submitPending}>{S.submitLabel}</SubmitButton>
      </DialogFooter>
    </form>
  );
}
